import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getFoodCourtItems, deleteFoodCourtItem } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Clock,
  Utensils,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

export default function FoodCourtPage() {
  useRequireSectionVisible("showFoodCourt");
  const { data: user } = useAuth();
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteFoodCourtItemFn = useAction(deleteFoodCourtItem);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch items with hub aggregation support
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getFoodCourtItems, {
    locationIds: getLocationIdsForQuery(),
    filters: {
      type: "FOOD_COURT",
    },
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.metadata?.cuisine
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item: any) => item.metadata?.category === selectedCategory,
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "price":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteFoodCourtItemFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      FOOD: "bg-secondary/10 text-secondary border border-secondary/20",
      BEVERAGE: "bg-primary/10 text-primary border border-primary/20",
      DESSERT: "bg-accent/10 text-accent border border-accent/20",
      SNACK: "bg-warning/10 text-warning border border-warning/20",
    };
    return (
      colors[category as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      AVAILABLE: "bg-success/10 text-success border border-success/20",
      LIMITED: "bg-warning/10 text-warning border border-warning/20",
      OUT_OF_STOCK:
        "bg-destructive/10 text-destructive border border-destructive/20",
    };
    return (
      colors[availability as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Food Court</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Discover food and beverages available at{" "}
            {selectedLocation?.name || "Location"} ({filteredItems.length} items)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Categories</option>
                    <option value="FOOD">Food</option>
                    <option value="BEVERAGE">Beverage</option>
                    <option value="DESSERT">Dessert</option>
                    <option value="SNACK">Snack</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">Title A-Z</option>
                    <option value="price">Price (High-Low)</option>
                  </select>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setIsFilterExpanded(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(
                    `/locations/${selectedLocation.locationCode}/food-court/add`,
                  );
                } else {
                  navigate("/food-court/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">Add Item</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-3 min-w-0 truncate">Item</div>
            <div className="col-span-1 min-w-0 truncate">Category</div>
            <div className="col-span-2 min-w-0 truncate">Cuisine</div>
            <div className="col-span-1 min-w-0 truncate">Price</div>
            <div className="col-span-2 min-w-0 truncate">Availability</div>
            <div className={user?.isAdmin ? "col-span-1 min-w-0 truncate" : "col-span-2 min-w-0 truncate"}>Author</div>
            {user?.isAdmin && <div className="col-span-2 text-right min-w-0 truncate">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search food items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-1">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="FOOD">Food</option>
              <option value="BEVERAGE">Beverage</option>
              <option value="DESSERT">Dessert</option>
              <option value="SNACK">Snack</option>
            </select>
          </div>
          <div className="col-span-2">
            <input
              type="text"
              placeholder="Cuisine"
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="col-span-1">
            <select
              value={sortBy === "price" ? "price" : "all"}
              onChange={(e) =>
                setSortBy(e.target.value === "price" ? "price" : "newest")
              }
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="price">High-Low</option>
            </select>
          </div>
          <div className="col-span-2">
            <select className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="all">All</option>
              <option value="AVAILABLE">Available</option>
              <option value="LIMITED">Limited</option>
              <option value="OUT_OF_STOCK">Out of Stock</option>
            </select>
          </div>
          <div className={user?.isAdmin ? "col-span-1" : "col-span-2"}>
            <select
              value={
                sortBy === "newest" || sortBy === "oldest"
                  ? sortBy
                  : "newest"
              }
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
            </select>
          </div>
          {user?.isAdmin && <div className="col-span-2" />}
        </div>
      </div>
    </div>
  );

  if (!user || !selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {!user ? "Authentication Required" : "No Location Selected"}
            </h2>
            <p className="text-gray-600">
              {!user 
                ? "You must be logged in to view food court items." 
                : "Please select a location to view food court items."}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading items...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Utensils className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Error Loading Items
                </h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Utensils className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Food Items Found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory !== "all"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "Be the first to add a food item in this location!"}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredItems.length > 0 && (
            <>
              {/* Cards View */}
              {viewMode === "cards" && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className={enhancedPageLayoutStyles.card.container}
                        onClick={() => navigate(`/food-court/${item.id}`)}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              <span className={cn("px-2 py-0.5 rounded text-xs font-medium", getCategoryColor(item.metadata?.category))}>
                                {item.metadata?.category}
                              </span>
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              {item.metadata?.price ? `$${item.metadata.price}` : "Free"}
                            </div>
                          </div>
                          <h3 className={enhancedPageLayoutStyles.card.title}>{item.title}</h3>
                          <p className={enhancedPageLayoutStyles.card.description}>{item.description}</p>
                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              by {item.author.name || item.author.email}
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e?.stopPropagation()}>
                              <EnhancedIconButton
                                onClick={() => navigate(`/food-court/${item.id}`)}
                                icon={Eye}
                                title="View details"
                              />
                              {user?.isAdmin && (
                                <EnhancedIconButton
                                  onClick={() => handleDelete(item.id)}
                                  icon={Trash2}
                                  title="Delete"
                                  variant="danger"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="p-6 space-y-4">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={enhancedPageLayoutStyles.list.item}
                      onClick={() => navigate(`/food-court/${item.id}`)}
                    >
                      <div className={enhancedPageLayoutStyles.list.content}>
                        <div className={enhancedPageLayoutStyles.list.left}>
                          <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                            <Utensils className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {item.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium", getCategoryColor(item.metadata?.category))}>
                                {item.metadata?.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                {item.author.name || item.author.email} • {new Date(item.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={enhancedPageLayoutStyles.list.right}>
                          <div className="text-right mr-4">
                            <span className="block text-sm font-bold text-green-600">
                              {item.metadata?.price ? `$${item.metadata.price}` : "Free"}
                            </span>
                          </div>
                          <EnhancedIconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/food-court/${item.id}`);
                            }}
                            icon={Eye}
                            title="View"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === "table" && (
                <div className="w-full">
                  <div className={enhancedPageLayoutStyles.table.container}>
                    <div className={enhancedPageLayoutStyles.table.wrapper}>
                      <table className={enhancedPageLayoutStyles.table.table}>
                        <tbody className={enhancedPageLayoutStyles.table.tbody}>
                          {filteredItems.map((item) => (
                            <tr
                              key={item.id}
                              className="group hover:bg-sidebar-hover transition-colors cursor-pointer"
                              onClick={() => navigate(`/food-court/${item.id}`)}
                            >
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-3")}>
                                <div className="px-6 py-4">
                                  <div className="font-medium text-gray-900 truncate">{item.title}</div>
                                  <div className="text-xs text-gray-500 truncate">{item.description}</div>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4">
                                  <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getCategoryColor(item.metadata?.category))}>
                                    {item.metadata?.category}
                                  </span>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                <div className="px-6 py-4 text-xs text-gray-600">
                                  {item.metadata?.cuisine || "-"}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4 text-sm font-bold text-green-600">
                                  {item.metadata?.price ? `$${item.metadata.price}` : "-"}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                <div className="px-6 py-4">
                                  <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getAvailabilityColor(item.metadata?.availability))}>
                                    {item.metadata?.availability?.replace("_", " ") || "AVAILABLE"}
                                  </span>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, user?.isAdmin ? "col-span-1" : "col-span-2")}>
                                <div className="px-6 py-4 text-sm text-gray-600 truncate">
                                  {item.author.name || item.author.email}
                                </div>
                              </td>
                              {user?.isAdmin && (
                                <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                    <div className="px-6 py-4 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                      <EnhancedIconButton
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          navigate(`/food-court/${item.id}/edit`);
                                        }}
                                        icon={Edit}
                                        title="Edit"
                                      />
                                      <EnhancedIconButton
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          handleDelete(item.id);
                                        }}
                                        icon={Trash2}
                                        title="Delete"
                                        variant="danger"
                                      />
                                    </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
